import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { useContent } from "@/lib/useContent";

const lineVariants = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.1, delay: 0.4 + i * 0.12, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function Hero() {
  const { content } = useContent();
  const { hero, site } = content;

  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayY = useTransform(scrollYProgress, [0, 1], ["0%", "-20%"]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative h-[100svh] w-full overflow-hidden bg-[#080808]"
    >
      <motion.div style={{ y, scale }} className="absolute inset-0 will-change-transform">
        {hero.video ? (
          <motion.video
            initial={{ scale: 1.08, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 2.4, ease: [0.16, 1, 0.3, 1] }}
            src={hero.video}
            autoPlay
            muted
            loop
            playsInline
            className="w-full h-full object-cover grayscale-[30%]"
          />
        ) : (
          <motion.img
            initial={{ scale: 1.08, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 2.4, ease: [0.16, 1, 0.3, 1] }}
            src={hero.image}
            alt="Trevor"
            className="w-full h-full object-cover grayscale-[30%]"
          />
        )}
      </motion.div>

      <div className="absolute inset-0 bg-gradient-to-b from-[#080808]/70 via-[#080808]/40 to-[#080808]" />
      <div className="absolute inset-0 spotlight" />

      <motion.div
        style={{ y: overlayY }}
        className="pointer-events-none absolute right-6 md:right-16 top-28 md:top-40 border border-white/10 bg-black/40 backdrop-blur-md p-5 hidden md:block"
        initial={{ opacity: 0, x: 40 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1.6, duration: 1 }}
      >
        <div className="font-mono text-[10px] tracking-[0.3em] text-[#C67022] mb-2">{hero.currentlyLabel}</div>
        <div className="font-display text-lg leading-tight max-w-[220px]">{hero.currentlyBody}</div>
      </motion.div>

      <div className="relative z-10 h-full flex flex-col justify-end pb-16 md:pb-24 px-6 md:px-10 max-w-[1600px] mx-auto">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="font-mono text-[10px] md:text-[11px] tracking-[0.35em] uppercase text-white/60 mb-8 flex items-center gap-4"
        >
          <span className="w-8 h-px bg-[#C67022]" />
          {site.location} · {site.years}
        </motion.div>

        <h1
          data-testid="hero-headline"
          className="font-display font-black tracking-tighter leading-[0.88] text-[15vw] md:text-[11vw] lg:text-[9vw]"
        >
          {hero.lines.map((line, i) => (
            <span key={i} className="reveal-mask block">
              <motion.span
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="show"
                className="block"
              >
                {i % 2 === 1 ? <em className="italic text-[#C67022]">{line}</em> : line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4, duration: 1 }}
          className="mt-10 md:mt-14 flex flex-col md:flex-row md:items-end justify-between gap-6"
        >
          <p className="max-w-md text-white/60 text-base md:text-lg font-light leading-relaxed">
            {hero.subhead}
          </p>
          <a
            href="#manifest"
            data-testid="hero-cta-scroll"
            className="group inline-flex items-center gap-3 font-mono text-[11px] tracking-[0.3em] uppercase text-white/70 hover:text-[#C67022] transition-colors"
          >
            <span className="w-10 h-px bg-current transition-all group-hover:w-16" />
            Enter the room
          </a>
        </motion.div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-px bg-[#2C2C2C]" />
    </section>
  );
}
